import { CodeEditor } from '../common/CodeEditor';

export interface ToolExampleProps {
  toolName: string;
  isDefault: boolean;
  exampleUsageJson?: string | null;
}

type ToolExamplePayload = {
  input: Record<string, any>;
  output: Record<string, any>;
};

const defaultExamples: Record<string, ToolExamplePayload> = {
  // Time & Date
  get_current_datetime: {
    input: { timezone: 'UTC', format: 'iso' },
    output: { datetime: '2026-01-17T09:00:00Z', timestamp: 1737129600, timezone: 'UTC' },
  },
  format_datetime: {
    input: { input: '1737129600', inputFormat: 'unix', outputFormat: 'iso', timezone: 'UTC' },
    output: { datetime: '2026-01-17T09:00:00Z', timestamp: 1737129600, timezone: 'UTC' },
  },
  calculate_date_difference: {
    input: { date1: '2026-01-01T00:00:00Z', date2: '2026-01-17T00:00:00Z', unit: 'days' },
    output: { difference: 16, unit: 'days' },
  },
  calculate_time_difference: {
    input: { date1: '2026-01-01T00:00:00Z', date2: '2026-01-17T00:00:00Z', unit: 'seconds' },
    output: { difference: 1382400, unit: 'seconds' },
  },
  add_time: {
    input: { date: '2026-01-17T09:00:00Z', inputFormat: 'iso', days: 1, outputFormat: 'iso', timezone: 'UTC' },
    output: { datetime: '2026-01-18T09:00:00Z', timestamp: 1737216000, timezone: 'UTC' },
  },
  convert_timezone: {
    input: { date: '2026-01-17T09:00:00Z', inputFormat: 'iso', fromTimezone: 'UTC', toTimezone: 'America/New_York', outputFormat: 'iso' },
    output: { datetime: '2026-01-17T04:00:00-05:00', timestamp: 1737129600, timezone: 'America/New_York' },
  },

  // Memory & Storage
  write_to_scratchpad: { input: { key: 'notes', value: 'Remember this', append: false }, output: { success: true, key: 'notes', value: 'Remember this', message: 'Wrote key.' } },
  read_from_scratchpad: { input: { key: 'notes' }, output: { found: true, key: 'notes', value: 'Remember this', message: 'Key found.' } },
  list_scratchpad_keys: { input: { pattern: 'no*' }, output: { keys: ['notes'], count: 1 } },
  clear_scratchpad: { input: { key: 'notes' }, output: { success: true, message: "Cleared key 'notes'." } },

  // Task Management
  create_task: { input: { title: 'Write docs', priority: 'high', tags: ['docs'] }, output: { taskId: 'task_xxx', title: 'Write docs', status: 'pending', createdAt: '2026-01-19T00:00:00Z' } },
  update_task: { input: { taskId: 'task_xxx', status: 'in_progress' }, output: { taskId: 'task_xxx', title: 'Write docs', status: 'in_progress', updatedAt: '2026-01-19T00:10:00Z' } },
  list_tasks: { input: { status: 'all', limit: 50 }, output: { tasks: [], count: 0 } },
  complete_task: { input: { taskId: 'task_xxx' }, output: { taskId: 'task_xxx', status: 'completed', updatedAt: '2026-01-19T00:30:00Z' } },

  // Calculation
  calculate: { input: { expression: 'sqrt(16) + 2' }, output: { result: 6, expression: 'sqrt(16) + 2' } },
  convert_units: { input: { value: 1, fromUnit: 'km', toUnit: 'm' }, output: { result: 1000, fromUnit: 'km', toUnit: 'm' } },

  // Text Processing
  format_text: { input: { text: 'hello world', format: 'title' }, output: { text: 'Hello World' } },
  extract_text: { input: { text: 'Email me at test@example.com', extractType: 'email' }, output: { matches: ['test@example.com'], count: 1 } },
  search_text: { input: { text: 'one two one', query: 'one', caseSensitive: false }, output: { found: true, matches: [{ position: 0, length: 3, match: 'one' }], count: 2 } },

  // Data Format
  convert_json: { input: { data: '{"a":1}', fromFormat: 'json', toFormat: 'yaml' }, output: { data: 'a: 1', fromFormat: 'json', toFormat: 'yaml' } },
  validate_json: { input: { json: '{"a":1}' }, output: { valid: true, errors: [] } },

  // Whiteboard
  create_whiteboard: { input: { name: 'Plan', description: 'High-level plan' }, output: { whiteboardId: 'wb_xxx', name: 'Plan', createdAt: '2026-01-19T00:00:00Z' } },
  add_whiteboard_item: { input: { whiteboardId: 'wb_xxx', type: 'note', content: 'First idea', position: { x: 100, y: 200 } }, output: { whiteboardId: 'wb_xxx', itemId: 'wbi_xxx', createdAt: '2026-01-19T00:05:00Z' } },
  get_whiteboard: { input: { whiteboardId: 'wb_xxx' }, output: { whiteboardId: 'wb_xxx', name: 'Plan', items: [] } },

  // Web
  search_web: { input: { query: 'CoOrdio Cortex default tools', maxResults: 5, language: 'en' }, output: { results: [], count: 0 } },
};

const tryParseExample = (exampleUsageJson?: string | null): ToolExamplePayload | null => {
  if (!exampleUsageJson) return null;
  try {
    const parsed = JSON.parse(exampleUsageJson);
    if (parsed && typeof parsed === 'object' && parsed.input && parsed.output) {
      return parsed as ToolExamplePayload;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Displays an example input/output payload for a tool.
 */
export const ToolExample = ({ toolName, isDefault, exampleUsageJson }: ToolExampleProps) => {
  const parsed = tryParseExample(exampleUsageJson);
  const fallback = defaultExamples[toolName];
  const example = parsed ?? (isDefault ? fallback : null);

  if (!example) {
    return (
      <div className="rounded-lg border border-border p-4">
        <p className="text-sm text-muted-foreground">
          No example available for this tool.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="rounded-lg border border-border p-4">
        <p className="text-sm font-medium mb-2">
          Example Input
        </p>
        <CodeEditor value={JSON.stringify(example.input, null, 2)} language="json" height="220px" readOnly />
      </div>
      <div className="rounded-lg border border-border p-4">
        <p className="text-sm font-medium mb-2">
          Example Output
        </p>
        <CodeEditor value={JSON.stringify(example.output, null, 2)} language="json" height="220px" readOnly />
      </div>
    </div>
  );
};
